import type {
  CreateUseCaseBody,
  modelFeatureSchema,
  pricingUnitSchema,
  providerSchema,
  SmsSettingsBody,
  tariffCurrencySchema,
  useCaseCategorySchema,
  useCaseKindSchema,
} from '@majuscule/contracts';
import {
  assertNever,
  err,
  euroDecimalToMicroEur,
  ok,
  parseCallingCodes,
  parseSmsSender,
  type Result,
  type SmsEncoding,
  smsSettingsErrorMessage,
  validateSmsSettings,
} from '@majuscule/domain';
import type { z } from 'zod';
import { formatEur } from './format';

/**
 * Shared vocabulary of the use-case screens (« Cas d’usage », « Détail client »,
 * PRD 17). Shapes mirror the internal API JSON (serialized domain entities;
 * amounts in reads are integer micro-euros). Unions come from the contracts: a
 * new kind, unit or provider breaks the typecheck until every label handles it.
 */

export type UseCaseKind = z.infer<typeof useCaseKindSchema>;
/** Family of a kind, derived and never stored (domain `useCaseCategory`, lot 17). */
export type UseCaseCategory = z.infer<typeof useCaseCategorySchema>;
export type PricingUnit = z.infer<typeof pricingUnitSchema>;
export type Provider = z.infer<typeof providerSchema>;
export type ModelFeature = z.infer<typeof modelFeatureSchema>;
/** Currency of a catalog entry, chosen at its creation and shared by all its tariff versions. */
export type TariffCurrency = z.infer<typeof tariffCurrencySchema>;
/** Creation body of an AI use case: the variant carrying the execution mode (an SMS has none). */
export type AiUseCaseBody = Extract<CreateUseCaseBody, { executionMode: unknown }>;
export type AiUseCaseKind = AiUseCaseBody['kind'];
export type ExecutionMode = AiUseCaseBody['executionMode'];

/** A use case as the internal API returns it (serialized domain `UseCase`). */
export interface UseCase {
  id: string;
  key: string;
  kind: UseCaseKind;
  acceptsFiles: boolean;
  // Archived capabilities have a null owner, but they are excluded on read by
  // the repository (lot 15); this null stays defensive for display only.
  ownerClientId: string | null;
  modelId: string;
  params: { maxOutputTokens: number | null; temperature: number | null };
  executionMode: ExecutionMode;
  /** Sender and allowed calling codes of an SMS use case; null for the AI kinds (lot 17). */
  sms: SmsSettingsBody | null;
  defaultPricingRuleId: string | null;
  active: boolean;
  /** Title of its invoice lines (lot 18); null = the key. */
  invoiceName: string | null;
  /** Text printed under its invoice lines (lot 18). */
  invoiceDescription: string | null;
}

/** SMS settings of an SMS use case; null for any other kind, whatever the payload holds. */
export function smsSettingsOf(useCase: UseCase): SmsSettingsBody | null {
  return useCase.kind === 'sms' ? useCase.sms : null;
}

/** A catalog entry as the internal API returns it: an AI model or, for Vonage, a service (lot 17). */
export interface Model {
  id: string;
  provider: Provider;
  providerModelId: string;
  displayName: string;
  features: ModelFeature[];
  /** Currency of its tariffs, chosen at creation. */
  currency: TariffCurrency;
  active: boolean;
}

/**
 * Whether a catalog entry is an SMS service. The API keeps providers and
 * features coherent (Vonage carries exactly `sms`, an AI model never), so the
 * feature alone decides.
 */
export function isSmsService(model: Pick<Model, 'features'>): boolean {
  return model.features.includes('sms');
}

/** A pricing rule body as read from the API (amounts in integer micro-euros). */
export type PricingRuleBody =
  | { type: 'multiplier'; coefficient: string }
  | { type: 'fixed_per_request'; amountMicroEur: number }
  | { type: 'per_unit'; unit: PricingUnit; amountMicroEur: number };

export type RuleType = PricingRuleBody['type'];

export interface PricingRule {
  id: string;
  useCaseId: string;
  clientId: string | null;
  effectiveFrom: string;
  rule: PricingRuleBody;
}

export const KIND_LABELS: Record<UseCaseKind, string> = {
  text: 'Texte',
  image: 'Image',
  sms: 'SMS',
};

export const CATEGORY_LABELS: Record<UseCaseCategory, string> = {
  ai: 'Intelligence artificielle',
  communication: 'Communication',
};

/** A unit as it reads in a rule summary (« Par page : … », « Par SMS : … »). */
export const UNIT_LABELS: Record<PricingUnit, string> = {
  page: 'page',
  image: 'image',
  sms: 'SMS',
};

/** A unit as an option of the unit select. */
export const UNIT_OPTION_LABELS: Record<PricingUnit, string> = {
  page: 'Page',
  image: 'Image',
  sms: 'SMS',
};

/** Unit offered first: what a request of the kind counts (PDF pages, generated images, SMS). */
export const DEFAULT_UNIT: Record<UseCaseKind, PricingUnit> = {
  text: 'page',
  image: 'image',
  sms: 'sms',
};

/** Rule type offered first: an SMS is sold per SMS, an AI use case usually with a multiplier. */
export const DEFAULT_RULE_TYPE: Record<UseCaseKind, RuleType> = {
  text: 'multiplier',
  image: 'multiplier',
  sms: 'per_unit',
};

export const SMS_UNIT_HINT =
  '1 unité = 1 SMS : 160 caractères en GSM-7 (153 par SMS au-delà), 70 en Unicode (67 par SMS au-delà).';

/**
 * Units a `per_unit` rule may count for a kind. Billing multiplies the units of
 * the request whatever the unit says, so a mismatched unit would only mislabel
 * the price; the API refuses `sms` outside an SMS use case and the reverse.
 */
export function unitsForKind(kind: UseCaseKind): readonly PricingUnit[] {
  switch (kind) {
    case 'text':
      return ['page', 'image'];
    case 'image':
      return ['image'];
    case 'sms':
      return ['sms'];
    default:
      return assertNever(kind);
  }
}

/** Formats an integer micro-euro amount as a French euro string (comma decimal). */
export function formatMicroEur(amountMicroEur: number): string {
  return formatEur(amountMicroEur / 1_000_000);
}

/** Human, French summary of a pricing rule body. */
export function describeRule(rule: PricingRuleBody): string {
  switch (rule.type) {
    case 'multiplier':
      return `Multiplicateur × ${rule.coefficient.replace('.', ',')}`;
    case 'fixed_per_request':
      return `Forfait par requête : ${formatMicroEur(rule.amountMicroEur)}`;
    case 'per_unit':
      return `Par ${UNIT_LABELS[rule.unit]} : ${formatMicroEur(rule.amountMicroEur)}`;
    default:
      return assertNever(rule);
  }
}

/** Price per SMS of a rule billing per SMS, in micro-euros; null for any other rule. */
export function pricePerSms(rule: PricingRuleBody): number | null {
  switch (rule.type) {
    case 'multiplier':
    case 'fixed_per_request':
      return null;
    case 'per_unit':
      return rule.unit === 'sms' ? rule.amountMicroEur : null;
    default:
      return assertNever(rule);
  }
}

/**
 * The version in effect at `now` for a scope (`null` = the default rule): the
 * latest one already effective.
 */
export function ruleInEffect(
  rules: readonly PricingRule[],
  clientId: string | null,
  now: number,
): PricingRule | undefined {
  let best: PricingRule | undefined;
  for (const rule of rules) {
    const from = new Date(rule.effectiveFrom).getTime();
    if (rule.clientId !== clientId || from > now) {
      continue;
    }
    if (best === undefined || from > new Date(best.effectiveFrom).getTime()) {
      best = rule;
    }
  }
  return best;
}

/** Where the price applied to a client comes from (PRD 8.3): its override or the default rule. */
export type RuleSource = 'override' | 'default';

/** A rule source as it reads next to a price: « Prix estimé (surcharge client) : … ». */
export const RULE_SOURCE_LABELS: Record<RuleSource, string> = {
  override: 'surcharge client',
  default: 'prix par défaut',
};

/**
 * The rule actually applied today (PRD 8.3): client override first, else default.
 * A null client (a use case without owner) has no override: the default applies.
 */
export function applicableRule(
  rules: readonly PricingRule[],
  clientId: string | null,
  now: number,
): { rule: PricingRule; source: RuleSource } | null {
  const override = clientId === null ? undefined : ruleInEffect(rules, clientId, now);
  if (override !== undefined) {
    return { rule: override, source: 'override' };
  }
  const fallback = ruleInEffect(rules, null, now);
  if (fallback !== undefined) {
    return { rule: fallback, source: 'default' };
  }
  return null;
}

/** A euro amount as the API accepts it (`euroAmountSchema`): a dot, 6 decimals at most. */
export const EURO_AMOUNT_PATTERN = /^\d+(\.\d{1,6})?$/;

/** Micro-euros of a typed euro amount (domain decimal arithmetic, no float), or null if invalid. */
export function euroInputToMicroEur(raw: string): number | null {
  const amount = raw.trim();
  if (!EURO_AMOUNT_PATTERN.test(amount)) {
    return null;
  }
  const micro = euroDecimalToMicroEur(amount);
  return micro.ok ? micro.value : null;
}

// --- SMS (lot 17) ---------------------------------------------------------------

export const ENCODING_LABELS: Record<SmsEncoding, string> = {
  gsm7: 'GSM-7',
  ucs2: 'Unicode (UCS-2)',
};

export const SENDER_HELP =
  'Nom affiché sur le téléphone du destinataire : marque du client, 3 à 11 lettres ou chiffres. Les mots génériques (OTP, INFO, SMS…) sont refusés par les opérateurs français.';

export const CALLING_CODES_HELP =
  'Indicatifs pays séparés par des virgules (33 = France métropolitaine ; 262, 590, 594, 596 pour l’outre-mer). Les autres destinations sont refusées.';

/** Allowed calling codes proposed for a new SMS use case: metropolitan France. */
export const DEFAULT_CALLING_CODES = '33';

/**
 * Sender suggested from a client name: diacritics stripped (NFD), only Latin
 * letters and digits kept, 11 characters at most; '' when that leaves fewer than
 * 3 characters or no letter (the operator then types one).
 */
export function suggestSender(clientName: string): string {
  const candidate = clientName
    .normalize('NFD')
    .replace(/[^A-Za-z0-9]/g, '')
    .slice(0, 11);
  return candidate.length >= 3 && /[A-Za-z]/.test(candidate) ? candidate : '';
}

/**
 * Calling codes typed as « 33, 262 »: split on commas, semicolons or line breaks,
 * entries trimmed, empty ones dropped. Spaces do not separate codes: « 33 7 »
 * (meant as 337) stays one invalid entry instead of silently allowing +7.
 */
export function parseCallingCodesInput(raw: string): string[] {
  return raw
    .split(/[,;\n]/)
    .map((code) => code.trim())
    .filter((code) => code.length > 0);
}

/** Calling codes as typed in an input: « 33, 262 ». */
export function formatCallingCodes(codes: readonly string[]): string {
  return codes.join(', ');
}

/** Calling codes for display: « +33, +262 ». */
export function displayCallingCodes(codes: readonly string[]): string {
  return codes.map((code) => `+${code}`).join(', ');
}

/** French message when a typed sender breaks the domain rules (af2m); null when valid or still empty. */
export function senderProblem(raw: string): string | null {
  if (raw.trim().length === 0) {
    return null;
  }
  const parsed = parseSmsSender(raw);
  return parsed.ok ? null : smsSettingsErrorMessage(parsed.error);
}

/** French message when typed calling codes break the domain rules; null when valid or still empty. */
export function callingCodesProblem(raw: string): string | null {
  if (raw.trim().length === 0) {
    return null;
  }
  const parsed = parseCallingCodes(parseCallingCodesInput(raw));
  return parsed.ok ? null : smsSettingsErrorMessage(parsed.error);
}

/**
 * SMS settings typed by the operator, validated by the domain rules the API
 * applies (trimmed sender, distinct codes); otherwise the French message of the
 * first problem.
 */
export function readSmsSettings(
  sender: string,
  callingCodes: string,
): Result<SmsSettingsBody, string> {
  if (sender.trim().length === 0) {
    return err('L’expéditeur est obligatoire.');
  }
  const settings = validateSmsSettings({
    sender,
    allowedCallingCodes: parseCallingCodesInput(callingCodes),
  });
  if (!settings.ok) {
    return err(smsSettingsErrorMessage(settings.error));
  }
  return ok({
    sender: settings.value.sender,
    allowedCallingCodes: [...settings.value.allowedCallingCodes],
  });
}

// --- Display helpers of the use-case screens ------------------------------------

export const EXECUTION_MODE_LABELS: Record<ExecutionMode, string> = {
  realtime: 'Temps réel',
  batch: 'Par lots',
};

/**
 * Where a pricing-rule version stands at `now` within its scope: the version in
 * effect, a version dated later, or one replaced by a later effective version.
 */
export type RuleStatus = 'current' | 'scheduled' | 'superseded';

export function ruleStatus(
  rule: PricingRule,
  rules: readonly PricingRule[],
  now: number,
): RuleStatus {
  if (new Date(rule.effectiveFrom).getTime() > now) {
    return 'scheduled';
  }
  return ruleInEffect(rules, rule.clientId, now)?.id === rule.id ? 'current' : 'superseded';
}
